import { CheckCircle, Trophy, ArrowLeft, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';

export default function SummaryStep({ module, progress, enrollmentId, checkAttempts, assessmentSessionId }: {
  module: { displayLabel: string; displayOrder: string };
  progress: { videoPercent: number; checkScore: number; checkPassed: boolean; readinessComplete: boolean };
  enrollmentId: string;
  checkAttempts: { attemptNumber: number; score: number; passed: boolean }[];
  assessmentSessionId?: string;
}) {
  const navigate = useNavigate();
  const passedAttempt = checkAttempts.find(a => a.passed);

  return (
    <div className="space-y-4">
      <div className="text-center py-6">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-xl font-bold text-foreground">Module Complete!</h1>
        <p className="text-sm text-muted-foreground mt-1">{module.displayLabel}</p>
      </div>

      <div className="space-y-2">
        <SummaryRow label="Video Watched" value={`${progress.videoPercent}%`} done />
        <SummaryRow label="Key Points Reviewed" value="Complete" done />
        <SummaryRow label="Scenarios Practised" value="Complete" done />
        <SummaryRow label="Knowledge Check" value={passedAttempt ? `${passedAttempt.score}% (${checkAttempts.length} attempt${checkAttempts.length > 1 ? 's' : ''})` : 'Skipped'} done={!!passedAttempt} />
        <SummaryRow label="Practical Readiness" value={progress.readinessComplete ? 'Confirmed' : 'Pending'} done={progress.readinessComplete} />
      </div>

      {assessmentSessionId && (
        <Link
          to={`/train/assessment-result/${assessmentSessionId}`}
          className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-xl hover:bg-primary/10 transition-colors"
        >
          <ClipboardCheck className="w-5 h-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">View Assessment Result</p>
            <p className="text-xs text-muted-foreground">See your assessment outcome, evidence and action plan</p>
          </div>
        </Link>
      )}

      <div className="bg-muted rounded-xl p-4 text-center">
        <p className="text-sm text-foreground font-medium">Next Step</p>
        <p className="text-xs text-muted-foreground mt-1">
          {progress.readinessComplete
            ? 'Your supervisor will schedule your practical assessment when ready.'
            : 'Complete the readiness check to signal you are ready for practical assessment.'}
        </p>
      </div>

      <Button onClick={() => navigate(`/train/${enrollmentId}`)} variant="outline" className="w-full h-12 text-base">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Course
      </Button>
    </div>
  );
}

function SummaryRow({ label, value, done }: { label: string; value: string; done: boolean }) {
  return (
    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
      <div className="flex items-center gap-2">
        <CheckCircle className={`w-4 h-4 ${done ? 'text-primary' : 'text-muted-foreground'}`} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <span className={`text-sm font-medium ${done ? 'text-primary' : 'text-muted-foreground'}`}>{value}</span>
    </div>
  );
}
